import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {IconButton,Stack,Tooltip,Typography} from '@mui/material';
import {
  GridCellParams,
  GridColDef,
  GridValidRowModel,
} from '@mui/x-data-grid';
import {FDataGrid} from '../grid';
import {
  InvestmentBudgetLine,
  LookupItem,
} from './investmentbudget.models';

type Props={
  rows:InvestmentBudgetLine[];
  costcenters:LookupItem[];
  accounts:LookupItem[];
  categories:LookupItem[];
  editable:boolean;
  saving?:boolean;
  onRowsChange:(rows:InvestmentBudgetLine[])=>void;
  onSave:(rows:InvestmentBudgetLine[])=>Promise<void>;
};

export default class InvestmentBudgetGrid
extends React.PureComponent<Props>{

  private validate=(r:InvestmentBudgetLine)=>{
    if(!r.costcentercode) return 'Cost Center is required.';
    if(!r.accountcode) return 'Account is required.';
    if(!r.investmentcategory) return 'Investment Category is required.';
    if(!r.projectassetname.trim()) return 'Project / Asset is required.';
    if(Number(r.requestedamount)<=0) return 'Requested amount must be greater than zero.';
    if(!r.justification.trim()) return 'Justification is required.';
    if(!r.expectedbenefit.trim()) return 'Expected benefit is required.';
    if(
      r.plannedstartdate&&
      r.plannedenddate&&
      r.plannedenddate<r.plannedstartdate
    ) return 'End date cannot be earlier than start date.';
    return '';
  };

  private processRowUpdate=(
    n:GridValidRowModel,
    o:GridValidRowModel,
  ):InvestmentBudgetLine=>{
    const old=o as InvestmentBudgetLine;
    const qty=Number(n.quantity||0);
    const upr=Number(n.unitprice||0);
    const requested=Number(n.requestedamount||qty*upr||0);

    const updated:InvestmentBudgetLine={
      ...old,
      ...(n as InvestmentBudgetLine),
      id:old.id,
      lineid:old.lineid,
      quantity:qty,
      unitprice:upr,
      requestedamount:requested,
      recurringcostimpact:Number(n.recurringcostimpact||0),
      procurementrequired:Boolean(n.procurementrequired),
      _state:old._state==='new'?'new':'dirty',
      __dirty:true,
      __error:'',
    };

    updated.__error=this.validate(updated);

    this.props.onRowsChange(
      this.props.rows.map(
        r=>r.id===old.id?updated:r,
      ),
    );

    return updated;
  };

  private add=()=>{
    const temp=-Date.now();

    this.props.onRowsChange([
      ...this.props.rows,
      {
        id:temp,
        lineid:0,
        lineix:'',
        headerid:0,

        costcenterid:0,
        costcentercode:'',
        costcentername:'',

        accountid:0,
        accountcode:'',
        accountname:'',

        investmentcategory:'',
        projectassetname:'',
        implementingdepartment:'',
        fundingcode:'',
        procurementrequired:false,

        plannedstartdate:null,
        plannedenddate:null,
        usefullifemonths:0,

        quantity:1,
        unitprice:0,
        requestedamount:0,
        approvedamount:0,

        justification:'',
        expectedbenefit:'',
        recurringcostimpact:0,
        remarks:'',

        statecode:'DRAFT',
        statename:'Draft',
        stamp:'',
        status:1,

        _state:'new',
        __dirty:true,
        __error:'Cost Center is required.',
      },
    ]);
  };

  private markDeleted=()=>{
    const selected=this.props.rows.filter(
      r=>(r as any).__selected===true,
    );

    if(selected.length===0) return;

    this.props.onRowsChange(
      this.props.rows.map(
        r=>selected.some(s=>s.id===r.id)
          ?{...r,_state:'deleted',__dirty:true}
          :r,
      ),
    );
  };

  private save=async()=>{
    await this.props.onSave(
      this.props.rows.filter(
        r=>r.__dirty||
           r._state==='new'||
           r._state==='deleted',
      ),
    );
  };

  render(){
    const columns:GridColDef[]=[
      {
        field:'investmentcategory',
        headerName:'Category',
        width:170,
        editable:true,
        type:'singleSelect',
        valueOptions:this.props.categories.map(
          x=>({value:x.code,label:x.name}),
        ),
        cellClassName:'ib-cell-input',
      },
      {
        field:'projectassetname',
        headerName:'Project / Asset',
        width:240,
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'costcentercode',
        headerName:'Cost Center',
        width:180,
        editable:true,
        type:'singleSelect',
        valueOptions:this.props.costcenters.map(
          x=>({
            value:x.code,
            label:`${x.code} — ${x.name}`,
          }),
        ),
        cellClassName:'ib-cell-input',
      },
      {
        field:'accountcode',
        headerName:'Account',
        width:200,
        editable:true,
        type:'singleSelect',
        valueOptions:this.props.accounts.map(
          x=>({
            value:x.code,
            label:`${x.code} — ${x.name}`,
          }),
        ),
        cellClassName:'ib-cell-input',
      },
      {
        field:'implementingdepartment',
        headerName:'Implementing Department',
        width:220,
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'fundingcode',
        headerName:'Funding Source',
        width:150,
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'procurementrequired',
        headerName:'Procurement',
        width:120,
        type:'boolean',
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'plannedstartdate',
        headerName:'Start Date',
        width:130,
        type:'date',
        editable:true,
        valueGetter:(v:any)=>v?new Date(v):null,
        cellClassName:'ib-cell-input',
      },
      {
        field:'plannedenddate',
        headerName:'End Date',
        width:130,
        type:'date',
        editable:true,
        valueGetter:(v:any)=>v?new Date(v):null,
        cellClassName:'ib-cell-input',
      },
      {
        field:'usefullifemonths',
        headerName:'Useful Life (Months)',
        width:150,
        type:'number',
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'quantity',
        headerName:'Qty',
        width:90,
        type:'number',
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'unitprice',
        headerName:'Unit Price',
        width:140,
        type:'number',
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'requestedamount',
        headerName:'Requested',
        width:150,
        type:'number',
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'approvedamount',
        headerName:'Approved',
        width:150,
        type:'number',
        editable:false,
        cellClassName:'ib-cell-computed',
      },
      {
        field:'recurringcostimpact',
        headerName:'Annual Recurring Impact',
        width:180,
        type:'number',
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'justification',
        headerName:'Justification',
        width:260,
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'expectedbenefit',
        headerName:'Expected Benefit',
        width:260,
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'remarks',
        headerName:'Remarks',
        width:220,
        editable:true,
        cellClassName:'ib-cell-input',
      },
      {
        field:'statecode',
        headerName:'State',
        width:120,
        editable:false,
        cellClassName:'ib-cell-computed',
      },
      {
        field:'__error',
        headerName:'Validation',
        width:240,
        editable:false,
        cellClassName:'ib-cell-error',
        renderCell:p=>p.value
          ?<Typography variant="caption" color="error.main">
             {p.value}
           </Typography>
          :null,
      },
      {
        field:'actions',
        headerName: 'Actions',
        width: 120,
        editable: false,
      }
    ];

    const dirty=this.props.rows.filter(r=>r.__dirty).length;
    const invalid=this.props.rows.filter(r=>r.__error).length;

    const toolbarActions=this.props.editable
      ?[
          {
            id:'add',
            label:'Add Investment',
            icon:<AddIcon fontSize="small"/>,
            onClick:this.add,
          },
          {
            id:'save',
            label:'Save Draft',
            icon:<SaveIcon fontSize="small"/>,
            disabled:
              dirty===0||
              invalid>0||
              !!this.props.saving,
            onClick:this.save,
          },
        ]
      :[];

    return (
      <FDataGrid
        rows={this.props.rows}
        columns={columns}
        getRowId={(r:InvestmentBudgetLine)=>r.id}
        height={560}

        title="Investment Budget Lines"
        subtitle="Amber cells are planner inputs. Blue cells are computed or governed."

        toolbarActions={toolbarActions as any}

        isCellEditable={(p:GridCellParams)=>
          this.props.editable&&
          [
            'investmentcategory',
            'projectassetname',
            'costcentercode',
            'accountcode',
            'implementingdepartment',
            'fundingcode',
            'procurementrequired',
            'plannedstartdate',
            'plannedenddate',
            'usefullifemonths',
            'quantity',
            'unitprice',
            'requestedamount',
            'recurringcostimpact',
            'justification',
            'expectedbenefit',
            'remarks',
          ].includes(p.field)
        }

        processRowUpdate={this.processRowUpdate}
        onProcessRowUpdateError={console.error}

        getRowClassName={p=>
          p.row.__error
            ?'ib-row-error'
            :p.row.__dirty
              ?'ib-row-dirty'
              :'ib-row-clean'
        }

        sx={{
          '& .ib-cell-input':{
            bgcolor:'warning.lighter',
          },
          '& .ib-cell-computed':{
            bgcolor:'info.lighter',
            color:'text.secondary',
          },
          '& .ib-row-dirty':{
            boxShadow:'inset 3px 0 0 0',
            boxShadowColor:'warning.main',
          },
          '& .ib-row-error':{
            bgcolor:'error.lighter',
          },
        }}
      />
    );
  }
}
